import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { MdAccountBalance, MdBusiness, MdClose, MdInventory2, MdLocationOn, MdReceiptLong } from 'react-icons/md';
import { getData, postData, putData } from '../../Webservice/Webservice';
import { indianStates } from '../../Utils/states';
import { BUSINESS_TYPES, FONT, GST_RX, IFSC_RX, PAN_RX, PAY_MODES, authToken } from './supplierShared';

const UPI_RX = /^[\w.-]{2,}@[a-zA-Z]{3,}$/;
const SWIFT_RX = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

const emptyForm = {
  name: '', contactPerson: '', phone: '', email: '', businessType: '', status: 'active',
  panNumber: '', gstNumber: '', tradeLicense: '',
  billingAddress: { street: '', city: '', state: '', country: 'India', pincode: '' },
  bankDetails: { accountHolder: '', accountNumber: '', ifsc: '', swift: '', upiId: '', preferredMode: '' },
};

const Section = ({ title, hint, accent, icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}` }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{hint}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

// `supplier` set = edit, empty = create
function SupplierForm({ open, supplier, onClose, onSaved }) {
  const isEdit = !!supplier;
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState([]); // [{ _id, title, moq, leadTime }]
  const [options, setOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // fill the form each time the popup opens
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setFormError('');
    if (supplier) {
      setForm({
        ...emptyForm,
        name: supplier.name || '', contactPerson: supplier.contactPerson || '', phone: supplier.phone || '', email: supplier.email || '',
        businessType: supplier.businessType || '', status: supplier.status || 'active',
        panNumber: supplier.panNumber || '', gstNumber: supplier.gstNumber || '', tradeLicense: supplier.tradeLicense || '',
        billingAddress: { ...emptyForm.billingAddress, ...(supplier.billingAddress || {}), country: supplier.billingAddress?.country || 'India' },
        bankDetails: { ...emptyForm.bankDetails, ...(supplier.bankDetails || {}), preferredMode: supplier.bankDetails?.preferredMode || '' },
      });
      setSelected((supplier.products || []).map((p) => {
        const prod = p.productId && typeof p.productId === 'object' ? p.productId : { _id: p.productId };
        return { ...prod, moq: p.moq || 0, leadTime: p.leadTime || 0 };
      }));
    } else {
      setForm(emptyForm);
      setSelected([]);
    }
    setLoadingProducts(true);
    getData('product/list?limit=all', null, authToken(), false)
      .then((res) => setOptions(Array.isArray(res?.products) ? res.products : []))
      .catch(() => setOptions([]))
      .finally(() => setLoadingProducts(false));
  }, [open, supplier]);

  const set = (path, value) => {
    setForm((prev) => {
      if (!path.includes('.')) return { ...prev, [path]: value };
      const [parent, child] = path.split('.');
      return { ...prev, [parent]: { ...prev[parent], [child]: value } };
    });
    if (errors[path]) setErrors((e) => ({ ...e, [path]: undefined }));
  };
  const onText = (e) => {
    let { name, value } = e.target;
    if (['panNumber', 'gstNumber', 'bankDetails.ifsc', 'bankDetails.swift'].includes(name)) value = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    set(name, value);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Business name is required';
    if (!form.contactPerson.trim()) e.contactPerson = 'Contact person is required';
    const phone = form.phone.replace(/\D/g, '');
    if (!phone) e.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(phone)) e.phone = 'Enter 10 digits';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!form.businessType) e.businessType = 'Choose a business type';
    if (!form.panNumber.trim()) e.panNumber = 'PAN is required';
    else if (!PAN_RX.test(form.panNumber)) e.panNumber = 'A PAN looks like ABCDE1234F';
    if (form.gstNumber && !GST_RX.test(form.gstNumber)) e.gstNumber = 'That is not a valid 15-character GST number';
    const a = form.billingAddress;
    if (!a.street.trim()) e['billingAddress.street'] = 'Street address is required';
    if (!a.city.trim()) e['billingAddress.city'] = 'City is required';
    if (!a.state) e['billingAddress.state'] = 'State is required';
    if (!a.pincode.trim()) e['billingAddress.pincode'] = 'Pincode is required';
    else if (!/^\d{4,6}$/.test(a.pincode.trim())) e['billingAddress.pincode'] = 'Enter a valid pincode';
    if (!a.country.trim()) e['billingAddress.country'] = 'Country is required';
    const b = form.bankDetails;
    if (!b.accountHolder.trim()) e['bankDetails.accountHolder'] = 'Account holder is required';
    const acct = b.accountNumber.replace(/\s|-/g, '');
    if (!acct) e['bankDetails.accountNumber'] = 'Account number is required';
    else if (!/^\d{6,24}$/.test(acct)) e['bankDetails.accountNumber'] = 'Enter 6 to 24 digits';
    if (!b.ifsc.trim()) e['bankDetails.ifsc'] = 'IFSC is required';
    else if (!IFSC_RX.test(b.ifsc.trim())) e['bankDetails.ifsc'] = 'An IFSC looks like HDFC0001234';
    if (b.swift && !SWIFT_RX.test(b.swift)) e['bankDetails.swift'] = 'A SWIFT code is 8 or 11 characters';
    if (b.upiId && !UPI_RX.test(b.upiId)) e['bankDetails.upiId'] = 'A UPI ID looks like name@bank';
    if (!b.preferredMode) e['bankDetails.preferredMode'] = 'Choose how you pay this supplier';
    if (selected.length === 0) e.products = 'Add at least one product';
    setErrors(e);
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setFormError('');
    const found = validate();
    if (Object.keys(found).length) return setFormError('Please fix the highlighted fields.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        phone: form.phone.replace(/\D/g, ''),
        products: selected.map((p) => ({ productId: p._id, moq: Number(p.moq) || 0, leadTime: Number(p.leadTime) || 0 })),
      };
      if (isEdit) await putData(`supplier/update/${supplier._id}`, payload, authToken(), true);
      else await postData('supplier/add', payload, authToken(), true);
      onSaved();
    } catch (err) {
      setFormError(err?.message || 'Could not save the supplier');
    } finally {
      setSaving(false);
    }
  };

  const f = (name, label, extra = {}) => {
    const val = name.includes('.') ? form[name.split('.')[0]][name.split('.')[1]] : form[name];
    return (
      <TextField fullWidth size="small" label={label} name={name} value={val ?? ''} onChange={onText} error={!!errors[name]} helperText={errors[name] || extra.helper} {...extra.props} />
    );
  };

  const totalMoq = useMemo(() => selected.reduce((a, p) => a + (Number(p.moq) || 0), 0), [selected]);

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="lg" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f11f,#6366f108)', borderBottom: '3px solid #6366f1' }}>
        <Avatar sx={{ bgcolor: '#6366f1', width: 44, height: 44 }}><MdBusiness size={22} /></Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 21, color: '#0f172a', lineHeight: 1.2 }}>{isEdit ? 'Edit supplier' : 'Add supplier'}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{isEdit ? `${supplier.name}${supplier.supplierId ? ` · ${supplier.supplierId}` : ''}` : 'Someone you buy stock from.'}</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small"><MdClose size={20} /></IconButton>
      </Box>

      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'contents' }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Section title="Business" hint="Who they are and how to reach them" accent="#6366f1" icon={MdBusiness}>
              <Grid container spacing={2.25}>
                <Grid item xs={12} md={6}>{f('name', 'Business name *')}</Grid>
                <Grid item xs={12} md={6}>{f('contactPerson', 'Contact person *')}</Grid>
                <Grid item xs={12} sm={6} md={3}>{f('phone', 'Phone *', { helper: '10 digits', props: { inputProps: { maxLength: 12 } } })}</Grid>
                <Grid item xs={12} sm={6} md={3}>{f('email', 'Email *', { props: { type: 'email' } })}</Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {f('businessType', 'Business type *', { props: { select: true, children: BUSINESS_TYPES.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>) } })}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {f('status', 'Status', { props: { select: true, children: [<MenuItem key="a" value="active">Active</MenuItem>, <MenuItem key="i" value="inactive">Inactive</MenuItem>] }, helper: 'Inactive suppliers are hidden from new purchases' })}
                </Grid>
              </Grid>
            </Section>

            <Section title="Tax & license" hint="Used on purchase invoices" accent="#0ea5e9" icon={MdReceiptLong}>
              <Grid container spacing={2.25}>
                <Grid item xs={12} sm={4}>{f('panNumber', 'PAN *', { props: { inputProps: { maxLength: 10, style: { fontFamily: 'monospace' } } } })}</Grid>
                <Grid item xs={12} sm={4}>{f('gstNumber', 'GST number', { helper: 'Optional', props: { inputProps: { maxLength: 15, style: { fontFamily: 'monospace' } } } })}</Grid>
                <Grid item xs={12} sm={4}>{f('tradeLicense', 'Trade license', { helper: 'Optional' })}</Grid>
              </Grid>
            </Section>

            <Section title="Billing address" hint="Where invoices are addressed" accent="#10b981" icon={MdLocationOn}>
              <Grid container spacing={2.25}>
                <Grid item xs={12}>{f('billingAddress.street', 'Street address *', { props: { multiline: true, rows: 2 } })}</Grid>
                <Grid item xs={12} sm={6} md={3}>{f('billingAddress.city', 'City *')}</Grid>
                <Grid item xs={12} sm={6} md={3}>{f('billingAddress.state', 'State *', { props: { select: true, children: indianStates.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>) } })}</Grid>
                <Grid item xs={12} sm={6} md={3}>{f('billingAddress.pincode', 'Pincode *', { props: { inputProps: { maxLength: 6 } } })}</Grid>
                <Grid item xs={12} sm={6} md={3}>{f('billingAddress.country', 'Country *')}</Grid>
              </Grid>
            </Section>

            <Section title="Bank & payment" hint="Where you send the money" accent="#f59e0b" icon={MdAccountBalance}>
              <Grid container spacing={2.25}>
                <Grid item xs={12} sm={6} md={4}>{f('bankDetails.accountHolder', 'Account holder *')}</Grid>
                <Grid item xs={12} sm={6} md={4}>{f('bankDetails.accountNumber', 'Account number *', { props: { inputProps: { inputMode: 'numeric' } } })}</Grid>
                <Grid item xs={12} sm={6} md={4}>{f('bankDetails.ifsc', 'IFSC *', { props: { inputProps: { maxLength: 11, style: { fontFamily: 'monospace' } } } })}</Grid>
                <Grid item xs={12} sm={6} md={4}>{f('bankDetails.swift', 'SWIFT code', { helper: 'Optional · for foreign payments' })}</Grid>
                <Grid item xs={12} sm={6} md={4}>{f('bankDetails.upiId', 'UPI ID', { helper: 'Optional' })}</Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {f('bankDetails.preferredMode', 'Preferred payment mode *', { props: { select: true, children: PAY_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>) } })}
                </Grid>
              </Grid>
            </Section>

            <Section title="Products & terms" hint="What they supply, the smallest order they accept, and how long delivery takes" accent="#ec4899" icon={MdInventory2}>
              <Autocomplete
                multiple
                options={options}
                loading={loadingProducts}
                value={selected}
                disableCloseOnSelect
                filterSelectedOptions
                getOptionLabel={(o) => o?.title || 'Product'}
                isOptionEqualToValue={(o, v) => String(o?._id) === String(v?._id)}
                onChange={(_, value) => {
                  setSelected(value.map((v) => selected.find((p) => p._id === v._id) || { ...v, moq: 0, leadTime: 0 }));
                  if (errors.products) setErrors((e) => ({ ...e, products: undefined }));
                }}
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Add products" placeholder="Search and select" error={!!errors.products} helperText={errors.products}
                    InputProps={{ ...params.InputProps, endAdornment: <>{loadingProducts ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />
                )}
              />
              {selected.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{selected.length} product{selected.length === 1 ? '' : 's'}</Typography>
                    <Chip size="small" label={`Total MOQ ${totalMoq}`} sx={{ fontFamily: FONT, fontWeight: 700 }} />
                  </Box>
                  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                    {selected.map((p, i) => (
                      <Box key={p._id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 90px 90px 32px', md: '1fr 140px 140px 36px' }, gap: 1.25, alignItems: 'center', p: 1.25, borderBottom: i < selected.length - 1 ? '1px solid #f1f5f9' : 'none', background: '#fff' }}>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.title}>{p.title || 'Product'}</Typography>
                        <TextField size="small" type="number" label="MOQ" value={p.moq} inputProps={{ min: 0 }} onChange={(e) => setSelected((list) => list.map((x) => (x._id === p._id ? { ...x, moq: e.target.value } : x)))} />
                        <TextField size="small" type="number" label="Lead days" value={p.leadTime} inputProps={{ min: 0 }} onChange={(e) => setSelected((list) => list.map((x) => (x._id === p._id ? { ...x, leadTime: e.target.value } : x)))} />
                        <IconButton size="small" aria-label="remove" onClick={() => setSelected((list) => list.filter((x) => x._id !== p._id))}><MdClose size={16} /></IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Section>

            {formError && <Alert severity="error">{formError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', borderRadius: '10px', px: 2.5 }}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 4, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>
            {saving ? 'Saving…' : isEdit ? 'Update supplier' : 'Add supplier'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default SupplierForm;
